/**
 * Payout Detail View
 * Shows detailed breakdown of a payout run: included/excluded jobs, totals, ledger links
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  TextField,
} from "@mui/material";
import {
  ArrowBack,
  ExpandMore,
  AccountBalance,
  PictureAsPdf,
  Email,
  Download,
} from "@mui/icons-material";
import {
  getPayoutRunById,
  getRunDeliveryOptions,
  initiatePayment,
  confirmProviderPayment,
  uploadPayoutPop,
  finalizePayoutSettlement,
  downloadPayoutOfferPdf,
} from "../services/payoutService.js";
import { getPayeeOptions } from "../../finance/services/payeeOptionsService.js";
import { logError } from "@/shared/utils/logger.js";
import {
  PageShell,
} from "@/shared/components/pageShell";

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "R0.00";
  return `R${Number(amount).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleString("en-ZA", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return dateString;
  }
};

/** Move / service date (date only) from ISO string */
const formatMoveDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-ZA", { dateStyle: "medium" });
  } catch {
    return "—";
  }
};

export default function PayoutDetailView() {
  const { payoutRunId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payoutRun, setPayoutRun] = useState(null);
  const [error, setError] = useState(null);
  const [payeeOptions, setPayeeOptions] = useState([]);
  const [delivery, setDelivery] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [settleBusy, setSettleBusy] = useState(false);
  const [settleMsg, setSettleMsg] = useState(null);
  const [settleErr, setSettleErr] = useState(null);
  const [popFile, setPopFile] = useState(null);

  // Load payee options for name lookup
  useEffect(() => {
    const loadPayeeOptions = async () => {
      try {
        const options = await getPayeeOptions();
        setPayeeOptions(options);
      } catch (err) {
        // Non-blocking error - we'll just show ID if name lookup fails
        logError("Failed to load payee options", { error: err.message });
      }
    };
    loadPayeeOptions();
  }, []);

  const fetchPayoutRun = useCallback(async () => {
    if (!payoutRunId) {
      setError("Payout run ID is required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getPayoutRunById(payoutRunId);
      if (result.success) {
        setPayoutRun(result.data);
      } else {
        setError(result.error || "Failed to load payout run");
      }
      const del = await getRunDeliveryOptions(payoutRunId);
      if (del.success) {
        const d = del.data?.data || del.data;
        setDelivery(d);
        if (d?.email) setEmailTo(d.email);
      }
    } catch (err) {
      logError("Failed to fetch payout run", {
        payoutRunId,
        error: err.message,
      });
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [payoutRunId]);

  useEffect(() => {
    fetchPayoutRun();
  }, [fetchPayoutRun]);

  // Create payee lookup map for efficient name resolution
  const payeeById = useMemo(() => {
    return new Map((payeeOptions || []).map((opt) => [opt.id, opt]));
  }, [payeeOptions]);

  // Get payee display info (name + ID)
  const getPayeeDisplay = (payeeId) => {
    const opt = payeeById.get(payeeId);
    const name = opt?.meta?.name || null;
    return {
      name: name || null,
      id: payeeId || "N/A",
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <PageShell sx={{ maxWidth: 1200, mx: "auto" }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/finance/payouts/history")} sx={{ mt: 2 }}>
          Back to History
        </Button>
      </PageShell>
    );
  }

  if (!payoutRun || !payoutRun.run) {
    return (
      <PageShell sx={{ maxWidth: 1200, mx: "auto" }}>
        <Alert severity="warning">Payout run not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/finance/payouts/history")} sx={{ mt: 2 }}>
          Back to History
        </Button>
      </PageShell>
    );
  }

  const { run, lines, jobTotals: backendJobTotals, jobSummaries } = payoutRun;

  // Use backend-provided jobTotals (no frontend money computation)
  // Fallback to empty array for backward compatibility with old payout runs
  const jobTotals = backendJobTotals
    ? Object.entries(backendJobTotals).map(([jobId, totals]) => ({
        jobId,
        ...totals,
        summary: jobSummaries?.[jobId] ?? null,
      }))
    : [];

  const payee = getPayeeDisplay(run.payeeId);
  const status = run.status || "offered";
  const canEmail = Boolean(delivery?.canEmail || (emailTo && emailTo.includes("@")));

  const runSettle = async (fn) => {
    setSettleBusy(true);
    setSettleErr(null);
    setSettleMsg(null);
    try {
      const result = await fn();
      if (!result.success) {
        setSettleErr(result.error || "Action failed");
      } else {
        setSettleMsg(result.message || "Updated");
        await fetchPayoutRun();
      }
    } catch (err) {
      setSettleErr(err.message);
    } finally {
      setSettleBusy(false);
    }
  };

  return (
    <PageShell sx={{ maxWidth: 1400, mx: "auto" }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() =>
            navigate(`/finance/payouts/provider/${encodeURIComponent(run.payeeId)}`)
          }
        >
          Provider desk
        </Button>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/finance/payouts/history")}>
          History
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Payout Run Details
        </Typography>
        <Chip label={run.id} variant="outlined" sx={{ fontFamily: "monospace" }} />
        <Chip
          label={status}
          color={
            status === "settled"
              ? "success"
              : status === "offered"
                ? "info"
                : "warning"
          }
        />
      </Box>

      {settleMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSettleMsg(null)}>
          {settleMsg}
        </Alert>
      )}
      {settleErr && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSettleErr(null)}>
          {settleErr}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Settlement
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Offer is already on the ledger. Use these steps for bank-truth closure.
          </Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Email To (override)"
              size="small"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              helperText={
                delivery?.email
                  ? `Resolved: ${delivery.email} (${delivery.emailSource || "profile"})`
                  : "No profile email — Download is primary"
              }
              sx={{ minWidth: 280 }}
            />
            <Button
              startIcon={<Download />}
              variant={!canEmail ? "contained" : "outlined"}
              onClick={() =>
                runSettle(async () => {
                  const r = await downloadPayoutOfferPdf(payoutRunId);
                  return r.success
                    ? { success: true, message: "Offer PDF downloaded" }
                    : r;
                })
              }
              disabled={settleBusy}
            >
              Download offer PDF
            </Button>
          </Stack>
          {!canEmail && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Email disabled until a valid To address is set. Download remains available.
            </Alert>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={settleBusy ? <CircularProgress size={16} /> : <Email />}
              disabled={
                settleBusy ||
                !["offered", "executed", "payment_initiated"].includes(status)
              }
              onClick={() =>
                runSettle(() =>
                  initiatePayment(payoutRunId, {
                    sendEmail: canEmail,
                    to: emailTo || undefined,
                  })
                )
              }
            >
              {status === "payment_initiated"
                ? "Resend offer email"
                : "Initiate payment"}
            </Button>
            <Button
              variant="outlined"
              disabled={settleBusy || status !== "payment_initiated"}
              onClick={() =>
                runSettle(() => confirmProviderPayment(payoutRunId, {}))
              }
            >
              Mark provider confirmed
            </Button>
            <Button
              component="label"
              variant="outlined"
              disabled={
                settleBusy ||
                status === "offered" ||
                status === "cancelled"
              }
            >
              {popFile ? popFile.name : "Choose POP"}
              <input
                type="file"
                hidden
                accept="application/pdf,image/*"
                onChange={(e) => setPopFile(e.target.files?.[0] || null)}
              />
            </Button>
            <Button
              variant="outlined"
              disabled={settleBusy || !popFile}
              onClick={() =>
                runSettle(async () => {
                  const r = await uploadPayoutPop(payoutRunId, popFile);
                  if (r.success) setPopFile(null);
                  return r.success
                    ? { success: true, message: "POP uploaded" }
                    : r;
                })
              }
            >
              Upload POP
            </Button>
            <Button
              variant="contained"
              color="success"
              disabled={
                settleBusy ||
                (status !== "provider_confirmed" && status !== "settled")
              }
              onClick={() =>
                runSettle(() =>
                  finalizePayoutSettlement(payoutRunId, {
                    sendEmail: canEmail,
                    to: emailTo || undefined,
                  })
                )
              }
            >
              {status === "settled" ? "Resend final pack" : "Finalize / settle"}
            </Button>
          </Stack>
          {run.pop?.filename && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              POP on file: {run.pop.filename}
              {run.pop.uploadedAt
                ? ` · ${formatDate(run.pop.uploadedAt)}`
                : ""}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Summary Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Payee
                  </Typography>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {payee.name || payee.id}
                    </Typography>
                    {payee.name && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {payee.id}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Offered At
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(run.executedAt)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Cycle Type
                  </Typography>
                  <Chip label={run.cycleType} size="small" />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={status}
                    size="small"
                    color={status === "settled" ? "success" : "default"}
                  />
                </Grid>
              </Grid>

              {run.cycleStart && run.cycleEnd && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Cycle Period
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(run.cycleStart)} - {formatDate(run.cycleEnd)}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Totals Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Totals
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Gross
                </Typography>
                <Typography variant="h6">{formatCurrency(run.totals?.gross)}</Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Platform Fee
                </Typography>
                <Typography variant="h6" color="info.main">
                  {formatCurrency(run.totals?.platformFee)}
                </Typography>
              </Box>
              {Number(run.totals?.recoveryDeduction || 0) > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Recovery deduction
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {formatCurrency(run.totals.recoveryDeduction)}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Net payable
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }} color="success.main">
                  {formatCurrency(
                    run.totals?.netAfterRecovery ?? run.totals?.net
                  )}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Jobs Summary */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Jobs Summary
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Included
                </Typography>
                <Typography variant="h6" color="success.main">
                  {run.includedJobs?.length || 0}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Excluded
                </Typography>
                <Typography variant="h6" color="error.main">
                  {run.excludedJobs?.length || 0}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Included Jobs */}
        {jobTotals.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Included Jobs ({jobTotals.length})
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Job ID</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Route</TableCell>
                        <TableCell>Move date</TableCell>
                        <TableCell align="right">Gross</TableCell>
                        <TableCell align="right">Platform Fee</TableCell>
                        <TableCell align="right">Net</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {jobTotals.map((job) => (
                        <TableRow key={job.jobId}>
                          <TableCell sx={{ fontFamily: "monospace" }}>{job.jobId}</TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {job.summary?.customer || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Typography variant="body2" color="text.secondary">
                              {job.summary?.routeLabel || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatMoveDate(job.summary?.moveDate)}</TableCell>
                          <TableCell align="right">{formatCurrency(job.gross)}</TableCell>
                          <TableCell align="right">{formatCurrency(job.platformFee)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatCurrency(job.net)}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              startIcon={<AccountBalance />}
                              onClick={() => navigate(`/finance/jobs/${job.jobId}/ledger`)}
                            >
                              View Ledger
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Excluded Jobs */}
        {run.excludedJobs && run.excludedJobs.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Excluded Jobs ({run.excludedJobs.length})
                </Typography>
                {run.excludedJobs.map((excluded, idx) => (
                  <Accordion key={idx} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                          width: "100%",
                          flexWrap: "wrap",
                        }}
                      >
                        <Chip
                          label={excluded.jobId}
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ fontFamily: "monospace" }}
                        />
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {payoutRun.jobSummaries?.[excluded.jobId]?.customer || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {payoutRun.jobSummaries?.[excluded.jobId]?.routeLabel || "—"}
                            {payoutRun.jobSummaries?.[excluded.jobId]?.moveDate
                              ? ` · ${formatMoveDate(payoutRun.jobSummaries[excluded.jobId].moveDate)}`
                              : ""}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {excluded.reasons?.length || 0} reason(s)
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Reasons:
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, m: 0 }}>
                          {(excluded.reasons || []).map((reason, reasonIdx) => (
                            <li key={reasonIdx}>
                              <Typography variant="body2">{reason}</Typography>
                            </li>
                          ))}
                        </Box>
                        <Button
                          size="small"
                          startIcon={<AccountBalance />}
                          onClick={() => navigate(`/finance/jobs/${excluded.jobId}/ledger`)}
                          sx={{ mt: 2 }}
                        >
                          View Job Ledger
                        </Button>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </PageShell>
  );
}
